// Sales CRM dashboard tile — "Opportunities at stakeholder risk".
// FE-SCRM-013 + BE-SCRM-014.
//
// Lists opportunities (in this entity, open stages only) whose
// coaching engine emitted at least one risk. Each row shows the
// stage + active risk-code chips so a producer can scan the book
// without drilling in. Clicking a row opens the opportunity detail.

import { Link } from "react-router-dom";

import { Badge, Card } from "@/design-system";
import {
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
} from "@/features/dashboard";

import { useStakeholderRisksQuery } from "./hooks";
import { RISK_LABEL } from "./labels";
import type { StakeholderRiskCode } from "./types";


interface Props {
  /** Optional producer filter — the Sales CRM landing surface passes
   * the current user's id so the tile defaults to "my book." Omit
   * to show the entire entity's at-risk opportunities (manager view). */
  producerId?: string;
}


// Stage labels for the row's secondary line. We keep the slug as the
// fallback so a new backend stage doesn't render as undefined.
const STAGE_LABEL: Record<string, string> = {
  lead: "Lead",
  meeting_set: "Meeting set",
  qualified: "Qualified",
  proposal: "Proposal",
  negotiation: "Negotiation",
  bor_signed_pending_effective_date: "BOR signed",
};


export function StakeholderRisksTile({ producerId }: Props) {
  const query = useStakeholderRisksQuery(
    producerId ? { producerId } : undefined,
  );

  return (
    <Card
      data-testid="stakeholder-risks-tile"
      data-section="stakeholder-risks"
      className="flex flex-col gap-3"
    >
      <header className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-md font-semibold text-text-primary">
            Opportunities at stakeholder risk
          </h3>
          <p className="mt-0.5 text-xs text-text-secondary">
            Pursuits with at least one active coaching risk.
          </p>
        </div>
        {query.data && (
          <Badge
            variant={query.data.total > 0 ? "regulatory" : "neutral"}
            data-testid="stakeholder-risks-count"
          >
            {query.data.total}
          </Badge>
        )}
      </header>

      {query.isLoading ? (
        <DashboardLoadingState variant="list" rows={3} />
      ) : query.isError ? (
        <DashboardErrorState
          title="Couldn't load stakeholder risks"
          retry={() => query.refetch()}
        />
      ) : query.data && query.data.rows.length === 0 ? (
        <DashboardEmptyState
          title="No risks right now"
          description="Every open pursuit has its stakeholders mapped cleanly."
        />
      ) : (
        <ol
          data-testid="stakeholder-risks-list"
          className="flex flex-col gap-2"
        >
          {query.data?.rows.map((row) => (
            <li
              key={row.opportunity_id}
              data-testid={`stakeholder-risk-row-${row.opportunity_id}`}
              className="rounded-md border border-border-subtle bg-raised p-3"
            >
              <Link
                to={`/dashboard/sales-crm/opportunities/${encodeURIComponent(row.opportunity_id)}`}
                className="flex flex-col gap-1 text-sm hover:opacity-90"
              >
                <span className="text-text-secondary text-xs uppercase tracking-wide">
                  {STAGE_LABEL[row.stage] ?? row.stage}
                </span>
                <div className="flex flex-wrap items-center gap-1">
                  {row.risks.map((r) => (
                    <Badge
                      key={r.code}
                      variant="regulatory"
                      title={
                        RISK_LABEL[r.code as StakeholderRiskCode]
                        ?? r.code
                      }
                      data-testid={`risk-chip-${row.opportunity_id}-${r.code}`}
                    >
                      {chipLabel(r.code as StakeholderRiskCode)}
                    </Badge>
                  ))}
                </div>
              </Link>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}


/** Short chip labels — the full sentence in ``RISK_LABEL`` is too
 * long for a Badge but lives on the ``title`` tooltip for hover
 * detail. */
function chipLabel(code: StakeholderRiskCode | string): string {
  switch (code) {
    case "missing_economic_buyer": return "No EB";
    case "missing_champion": return "No champion";
    case "champion_is_user_role_only": return "Champion low-influence";
    case "single_threaded": return "Single-threaded";
    case "opposed_stakeholder_unhandled": return "Opposed";
    case "cold_economic_buyer": return "EB cold";
    default: return code;
  }
}
